import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { ArrowLeft, Shield, Lock, Eye, UserCheck, Database } from 'lucide-react';

interface PrivacyTermsProps {
  onAccept: () => void;
  onBack: () => void;
  title?: string;
  showBackButton?: boolean;
}

export function PrivacyTerms({ 
  onAccept, 
  onBack, 
  title = "Privacy Policy & Terms of Service",
  showBackButton = true 
}: PrivacyTermsProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <Card className="border-0 shadow-2xl bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-4">
            <div className="flex items-center justify-between mb-2">
              {showBackButton && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onBack}
                  className="flex items-center space-x-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back</span>
                </Button>
              )}
              <div className="flex items-center space-x-2 mx-auto">
                <Shield className="w-6 h-6 text-primary" />
                <Lock className="w-6 h-6 text-primary" />
              </div>
            </div>
            <CardTitle className="text-xl font-bold text-primary">
              {title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-96 w-full rounded border p-4 mb-6">
              <div className="space-y-6 text-sm">
                {/* Privacy Policy Section */}
                <section>
                  <h3 className="font-semibold text-primary mb-3 flex items-center">
                    <Shield className="w-4 h-4 mr-2" />
                    Privacy Policy
                  </h3>
                  <div className="space-y-3 text-muted-foreground">
                    <p>
                      <strong className="text-foreground">Privacy First Approach:</strong> Clock-It is designed with your privacy as the top priority. 
                      We believe your time tracking data should remain private and under your control.
                    </p>
                    <p>
                      <strong className="text-foreground">Data Collection:</strong> We only collect data necessary for the app to function:
                    </p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Email address for account authentication</li>
                      <li>Time tracking entries you create</li>
                      <li>App preferences and settings</li>
                      <li>Basic usage analytics (anonymized)</li>
                    </ul>
                    <p>
                      <strong className="text-foreground">Data Storage:</strong> Your data is encrypted both in transit and at rest. 
                      We use industry-standard security measures to protect your information.
                    </p>
                    <p>
                      <strong className="text-foreground">Data Sharing:</strong> We never sell, rent, or share your personal data with third parties 
                      without your explicit consent. Your time tracking data is yours alone.
                    </p>
                    <p>
                      <strong className="text-foreground">Data Control:</strong> You can export, modify, or delete your data at any time 
                      through the app settings.
                    </p>
                  </div>
                </section>

                {/* Terms of Service Section */}
                <section>
                  <h3 className="font-semibold text-primary mb-3 flex items-center">
                    <UserCheck className="w-4 h-4 mr-2" />
                    Terms of Service
                  </h3>
                  <div className="space-y-3 text-muted-foreground">
                    <p>
                      <strong className="text-foreground">Acceptance:</strong> By creating an account and using Clock-It, 
                      you agree to these terms and our privacy policy.
                    </p>
                    <p>
                      <strong className="text-foreground">Service Description:</strong> Clock-It is a time tracking application 
                      designed to help you monitor and manage your work hours across different projects and clients.
                    </p>
                    <p>
                      <strong className="text-foreground">User Responsibilities:</strong>
                    </p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Provide accurate information during registration</li>
                      <li>Keep your login credentials secure</li>
                      <li>Use the service in compliance with applicable laws</li>
                      <li>Not attempt to reverse engineer or compromise the service</li>
                    </ul>
                    <p>
                      <strong className="text-foreground">Service Availability:</strong> We strive to maintain high uptime 
                      but cannot guarantee uninterrupted service. We may perform maintenance with reasonable notice.
                    </p>
                    <p>
                      <strong className="text-foreground">Account Termination:</strong> You may delete your account at any time. 
                      We may suspend accounts that violate these terms, with appropriate notice when possible.
                    </p>
                  </div>
                </section>

                {/* Security Measures */}
                <section>
                  <h3 className="font-semibold text-primary mb-3 flex items-center">
                    <Lock className="w-4 h-4 mr-2" />
                    Security Measures
                  </h3>
                  <div className="space-y-2 text-muted-foreground">
                    <div className="flex items-center space-x-2">
                      <Database className="w-3 h-3" />
                      <span>End-to-end encryption for sensitive data</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Shield className="w-3 h-3" />
                      <span>Secure authentication with email verification</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Lock className="w-3 h-3" />
                      <span>Regular security audits and updates</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Eye className="w-3 h-3" />
                      <span>Transparent data practices</span>
                    </div>
                  </div>
                </section>

                {/* Contact Information */}
                <section className="border-t pt-4">
                  <h4 className="font-medium text-foreground mb-2">Questions or Concerns?</h4>
                  <p className="text-muted-foreground text-xs">
                    If you have any questions about this Privacy Policy or Terms of Service, 
                    please contact us through the app's support section or via the settings menu.
                  </p>
                  <p className="text-muted-foreground text-xs mt-2">
                    <strong>Last updated:</strong> September 2025
                  </p>
                </section>
              </div>
            </ScrollArea>

            {/* Agreement Actions */}
            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                      Your Privacy is Protected
                    </p>
                    <p className="text-blue-700 dark:text-blue-300">
                      By accepting these terms, you acknowledge that you understand how we protect 
                      your data and agree to use Clock-It responsibly.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                {showBackButton && (
                  <Button
                    variant="outline"
                    onClick={onBack}
                    className="flex-1"
                  >
                    Back to Sign Up
                  </Button>
                )}
                <Button
                  onClick={onAccept}
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  I Accept & Continue
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}